import RecruitmentUserDetailConstants from '@switchsystems/coactive-node-api-recruitment-shared/src/constants/RecruitmentUserDetailConstants';
import { Auth, API } from 'aws-amplify';
import axios from 'axios';
import { merge } from 'lodash';
import { vuexErrorHandler, stripProperties, EventBus } from '@switchsystems/coactive-vue-lib';
import constants from './user.constants';

const API_NAME = 'users';
const API_VERSION = '/v1';

async function getApiInit() {
  return {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
    },
  };
}

const modulestate = () => ({
  jobTypes: constants.jobTypes,
  userDetailSections: constants.userDetailSections,
  maritalStatus: constants.maritalStatus,
  defaultUserDetails: constants.defaultUserDetails,
  blankAddress: constants.blankAddress,
  blankEmployment: constants.blankEmployment,
  employmentStatus: constants.employmentStatus,
  experienceOptions: RecruitmentUserDetailConstants.experienceOptions,
  blankQualification: constants.blankQualification,
  qualificationName: constants.qualificationName,
  qualificationLevel: constants.qualificationLevel,
  blankReference: constants.blankReference,
  yearsKnown: constants.yearsKnown,
  requiredFields: constants.requiredFields,
  /*
    A flag to indicate to store consumers that the userDetails object in it has changed recently.
    If you need to respond / change this, you should also subscribe to
    Eventbus.on('user/userDetailsDirty', ...)
  */
  userDetailsDirty: false,
});
const getters = {
  jobTypes: (state) => state.jobTypes,
  userDetailSections: (state) => state.userDetailSections,
  defaultUserDetails: (state) => state.defaultUserDetails,
  maritalStatus: (state) => state.maritalStatus,
  experienceOptions: (state) => state.experienceOptions,
  blankAddress: (state) => state.blankAddress,
  blankEmployment: (state) => state.blankEmployment,
  employmentStatus: (state) => state.employmentStatus,
  blankQualification: (state) => state.blankQualification,
  qualificationName: (state) => state.qualificationName,
  qualificationLevel: (state) => state.qualificationLevel,
  blankReference: (state) => state.blankReference,
  yearsKnown: (state) => state.yearsKnown,
  userDetailsDirty: (state) => state.userDetailsDirty,
  requiredFields: (state) => state.requiredFields,
};
const mutations = {
  userDetailsDirty(state, dirtyState) {
    if (dirtyState === true) {
      EventBus.$emit('user/userDetailsDirty', { value: true });
    }
    state.userDetailsDirty = dirtyState;
  },
};
const actions = {
  async updateBasicProfile(context, params) {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });

    // remove 'tel' if present -- that's old data
    myInit.body = stripProperties(params.changes, ['id', 'ref', 'tel', 'status', 'dateLastLogin', 'files']);

    return API.patch(API_NAME, `${API_VERSION}/users/me`, myInit)
      .then(() => {
        context.dispatch('auth/fetchUser', null, { root: true });
      })
      .catch((error) => vuexErrorHandler(error));
  },
  async getUserDetails(context) {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });

    const data = await API.get(API_NAME, `${API_VERSION}/users/details/me`, myInit)
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          return API.post(API_NAME, `${API_VERSION}/users/details/me`, myInit).catch(() => {
            // do nothing
          });
        }
        return vuexErrorHandler(error);
      });

    context.commit('userDetailsDirty', false);

    const result = merge(
      {},
      context.state.defaultUserDetails,
      data,
    );

    // CC-106
    // do we need to update the favourites list with the userState.applyForJob property?
    if (context.rootGetters['userstate/applyForJob'] !== '') {
      if (!result.bookmarkedJobOpportunities.includes(context.rootGetters['userstate/applyForJob'])) {
        // update userDetails
        result.bookmarkedJobOpportunities.push(context.rootGetters['userstate/applyForJob']);
        // do this in its own promise - so we can return early and not have to wait
        context.dispatch('updateUserDetails', { changes: { bookmarkedJobOpportunities: result.bookmarkedJobOpportunities } }).then(() => {
          context.commit('userstate/updateApplyForJob', '', { root: true });
        });
      } else {
        context.commit('userstate/updateApplyForJob', '', { root: true });
      }
    }
    return result;
  },
  async updateUserDetails(context, params) {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });

    myInit.body = params.changes;
    return API.patch(API_NAME, `${API_VERSION}/users/details/me`, myInit);
  },
  async uploadCvFile(context, params) {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });
    myInit.body = {
      filename: params.file.name,
      type: 'cv',
    };
    // generate the pre-signed url and file id for the upload
    const uploadDetails = await API.post(
      API_NAME,
      `${API_VERSION}/users/upload/me`,
      myInit,
    );
    // upload the file (plain axios request this is to aws s3 api)
    const uploadResult = await axios.put(uploadDetails.httpPutUrl, params.file);
    return {
      fileId: uploadDetails.fileId,
      uploadHttpStatus: uploadResult.status,
    };
  },
  async deleteCvFile(context, params) {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });
    return API.del(
      API_NAME,
      `${API_VERSION}/users/me/files/${params.fileId}`,
      myInit,
    );
  },
  async delete() {
    const myInit = await getApiInit().catch((error) => { vuexErrorHandler(error); throw error; });
    return API.del(
      API_NAME,
      `${API_VERSION}/users/me`,
      myInit,
    );
  },
};

export default {
  namespaced: true,
  mutations,
  getters,
  actions,
  state: modulestate,
};
